<script setup>
// Common page content for Location List pages (e.g. /user/favorites/locations/summary,
// /explore/state/US-CO/summary, /explore/pass/ikonpass/summary, etc). Specify the card component
// to render to display each location, a method to fetch pages of locations, the locations, the
// locations range, and the selected nav item, and optionally the selected tab.

import { checkCompareField } from '@@/components/Compare/Common/CompareUtils';
import { closest } from '@@/utils/LocationSortFields';
import { getLastItem } from '@@/utils/CommonUtils';
import { useExplorePage } from '@@/composables/useExplorePage';
import { useLocationsStore } from '@@/stores/Locations';
import { useUser } from '@@/composables/useUser';
import { useUserFavoritesStore } from '@@/stores/UserFavorites';

const props = defineProps({
  canShowSelectFavoriteListDialog: {
    type: Boolean,
    default: false,
  },
  cardComponent: {
    type: Object,
    required: true,
  },
  cardComponentProps: {
    type: Object,
    default: null,
  },
  defaultCompareField: {
    type: Object,
    default: null,
  },
  fetchLocationSkeletons: {
    type: Function,
    default() { return null; },
  },
  handleFetchLocations: {
    type: String,
    required: true,
  },
  isFavorites: {
    type: Boolean,
    default: false,
  },
  locationsField: {
    type: String,
    required: true,
  },
  rangeField: {
    type: String,
    default: null,
  },
  selectedNavItem: {
    type: Number,
    default: 0,
  },
  selectedTab: {
    type: Number,
    default: 0,
  },
  sortOptions: {
    type: Array,
    default: null,
  },
});

const { $loading, $toast } = useNuxtApp();
const { params } = useRoute();
const router = useRouter();

const locationsStore = useLocationsStore();
const userFavoritesStore = useUserFavoritesStore();

const { getErrorMessage, getUserLocation } = useUserLocation();
const dismissedSkeletonCount = useCookieWrapper('dismissed-skeleton-count');

// When navigating to a new compare view for the same type and ID, from one User Favorite
// Locations page to another, or to the Powder Finder, then do not display the <NoFavoritesMessage>
// component when the locations are briefly reset.
const isNavigatingToDifferentView = ref(false);
const isNavigatingToPowderFinder = ref(false);

// HACK: Change the compareFilterKey value to the current date time when unable to sort by closest
// to force the <CompareOptions> to re-render so that the closest sort option is no longer selected.
// If the <CompareOptions> were not re-rendered then closest would be selected by the locations
// would be sorted by the previous selection.
// SEE: https://michaelnthiessen.com/force-re-render
const compareFilterKey = ref(Date.now());

// Set to true when filtering or sorting so the No Favorites message isn't shown when updating
// locations in the same view.
const isUpdating = ref(false);

const showAddFavoritesDialog = ref(false);
const skeletons = ref(null);
const userLocation = ref(null);

const limit = ref(20);
const skip = ref(0);
const sort = ref(undefined);

const {
  compare,
  favoriteLocationTypes,
  handleDropdownClose,
  handleDropdownOpen,
  pageOverflow,
  pageParams,
  updateUrl,
} = useExplorePage();
const { isAllAccess, isGuest, units } = useUser();
const activeList = computed(() => userFavoritesStore.getActiveList);

let { filter, isFilterSet } = useCompareFilter({
  activeListId: activeList.value?.id,
  isFavorites: props.isFavorites,
});

const locations = computed(() => locationsStore[props.locationsField]);
const range = computed(() => locationsStore[props.rangeField]);

const {
  lastComparePageCookie,
  mapSortCookie,
  sortCookie,
  queryCompareField,
} = useCompareUtils(activeList.value);

const locationIdsForActiveList = computed(() => userFavoritesStore.getLocationIdsForActiveList);
const canDismissSkeleton = computed(() => props.isFavorites && locationIdsForActiveList.value?.length >= 1);

/**
 * If the selected sort option has a map field then it can be used to sort the map and will be
 * specified in the URL to the Compare/Favorites Map Page in the the Compare/Favorites
 * Header. By doing this, when the user has sorted the locations on the Compare/Favorites page,
 * and then navigates to the map, the locations on the map will be sorted in the same order as
 * they were being viewed on the Compare/Favorites page.
 */
const compareField = computed(() => {
  const sortOption = props.sortOptions.find((option) => option.field === sort.value);
  return sortOption?.mapField ? sortOption : props.defaultCompareField;
});

const hasLocations = computed(() => locations.value?.length > 0);

const pageContentStyle = computed(() => {
  let minHeight = '60vh';

  if ((isNavigatingToPowderFinder.value)
    || (props.isFavorites && locationIdsForActiveList.value?.length >= 3)
    || (hasLocations.value && props.locations?.length >= 3)
    || isUpdating.value) {
    minHeight = '100vh';
  }

  return {
    minHeight,
    overflow: pageOverflow.value,
  };
});

const navItems = computed(() => {
  const path = props.isFavorites
    ? '/user/favorites/locations'
    : `/explore/${compare.value?.compareType}/${compare.value?.compareId}`;

  return [
    {
      text: 'Weather',
      to: `${path}/weather`,
    },
    {
      text: 'Snow Summary',
      to: `${path}/snow-summary`,
    },
    {
      isLocked: !isAllAccess.value,
      text: 'Snow Forecast',
      to: `${path}/snow-forecast`,
    },
    {
      isLocked: !isAllAccess.value,
      text: 'Snow History',
      to: `${path}/snow-history`,
    },
    {
      text: 'Snow Report',
      to: `${path}/snow-report`,
    },
    {
      isLocked: !isAllAccess.value,
      text: 'All Cams',
      to: `${path}/all-cams`,
    },
    {
      text: 'Temperature',
      to: `${path}/temperature`,
    },
  ];
});

const showCompareMenu = computed(() => props.isFavorites
  ? locationIdsForActiveList.value?.length > 0
  : true);

const showNoFavoritesMessage = computed(() => {
  if (isUpdating.value) {
    return false;
  }

  if (props.isFavorites) {
    // Always show the No Favorites Message if there are no location IDs for the active list
    if (locationIdsForActiveList.value?.length === 0) {
      return true;
    }

    // Show the No Favorites Message if there are location IDs for the active list and the Add
    // Favorites Dialog is visible and skeletons are not being displayed.
    //
    // As locations are added, the location IDs for active list count will be greater than 0,
    // but the No Favorites Message should remain displayed until the Add Favorites Dialog is
    // closed, otherwise the Add Favorites Dialog will be unmounted before it finishes adding
    // all locations, and newly added locations will not be displayed in the Compare Locations
    // List page.
    if (locationIdsForActiveList.value?.length > 0
      && showAddFavoritesDialog.value === true
      && skeletons.value === null) {
      return true;
    }

    // Show the No Favorites Message if there are location IDs for the active list and the filter
    // is set. In this case, none of the locations match the selected filters and the user should
    // be informed of this case.
    if (locationIdsForActiveList.value?.length > 0 && isFilterSet.value) {
      return true;
    }

    // Otherwise don't show the No Favorites Message
    return false;
  }

  if (isNavigatingToPowderFinder.value && !hasLocations.value) {
    return false;
  }

  return !isNavigatingToDifferentView.value && !hasLocations.value;
});

const skeletonCount = computed(() => {
  let mySkeletonCount = 0;

  if (props.isFavorites) {
    if (locationIdsForActiveList.value.length === 0) {
      mySkeletonCount = 3;
    }
    else if (locationIdsForActiveList.value.length === 1) {
      mySkeletonCount = 2;
    }
    else if (locationIdsForActiveList.value.length === 2) {
      mySkeletonCount = 1;
    }
  }

  if (mySkeletonCount && typeof dismissedSkeletonCount.value === 'number') {
    mySkeletonCount = mySkeletonCount - dismissedSkeletonCount.value;
    mySkeletonCount = mySkeletonCount < 0 ? 0 : mySkeletonCount;
  }

  return mySkeletonCount;
});

const attemptToGetUserLocation = async (canShowLoading = true) => {
  if (canShowLoading) {
    $loading.start();
  }

  const options = { maximumAge: 10 * 60 * 2000 }; // 10 minutes (600,000 ms)

  let userLocation;
  let errorMessage;

  try {
    userLocation = await getUserLocation(options);
  }
  catch (e) {
    errorMessage = getErrorMessage(e);
  }

  if (!userLocation) {
    $loading.finish();

    $toast.open({
      message: `Unable to sort by closest! ${errorMessage}`,
      type: 'error',
    });

    return null;
  }

  return userLocation;
};

const fetchLocations = async (newLimit = 20, newSkip = 0, refresh = false) => {
  limit.value = newLimit;
  skip.value = newSkip;

  const { compareType, compareId } = params || {};

  let location_type_ids;

  if (filter.value?.locationTypes?.length) {
    location_type_ids = filter.value.locationTypes.map(({ id }) => id).join(',');
  }

  return locationsStore[props.handleFetchLocations]({
    compareType,
    compareId,
    limit: limit.value,
    list_id: props.isFavorites ? activeList.value?.id : null,
    location_type_ids,
    point: userLocation.value,
    refresh,
    skip: skip.value,
    sort: sort.value,
    units: units.value,
  });
};

const handleListChange = async () => {
  $loading.start();
  ({ filter, isFilterSet } = useCompareFilter({
    activeListId: activeList.value?.id,
    isFavorites: props.isFavorites,
  }));
  skip.value = 0;

  await locationsStore.resetLocations({ keepCompareOnReset: true });
  await fetchLocations();

  $loading.finish();
};

const handleFavoritesAdded = (countFavoritesAdded) => {
  if (countFavoritesAdded > 0) {
    handleListChange();
  }
};

const handleAddFavoritesDialogClose = async (payload) => {
  showAddFavoritesDialog.value = false;

  if (payload?.countFavoritesAdded > 0) {
    await userFavoritesStore.fetchUserFavoriteIds({ forceUpdate: true });
    skeletons.value = props.fetchLocationSkeletons(skeletonCount.value);
    handleFavoritesAdded(payload.countFavoritesAdded);
  }
};

const handleDismissSkeleton = () => {
  if (typeof dismissedSkeletonCount.value === 'number') {
    dismissedSkeletonCount.value += 1;
  }
  else {
    dismissedSkeletonCount.value = 1;
  }

  if (dismissedSkeletonCount.value > 2) {
    dismissedSkeletonCount.value = 2;
  }

  skeletons.value = props.fetchLocationSkeletons(skeletonCount.value);
};

const handleNavItemClick = ($event, navItem) => {
  const lastComparePage = navItem.to.split('/').slice(-1)[0];
  lastComparePageCookie.value = lastComparePage;
};

const handleShowAddFavoritesDialog = (showDialog) => showAddFavoritesDialog.value = showDialog;

const handleFilterChange = async ({ filter: newFilter, sort: newSort }) => {
  if (newSort != sort.value) {
    if (newSort === closest.field) {
      userLocation.value = await attemptToGetUserLocation();

      if (!userLocation.value) {
        compareFilterKey.value = Date.now();
        return;
      }

      $loading.finish();
    }
  }

  isUpdating.value = true;
  filter.value = newFilter;
  sort.value = newSort;
  skip.value = 0;

  await locationsStore.resetLocations({ keepCompareOnReset: true });
  await fetchLocations();

  sortCookie.value = newSort;
  const sortOption = props.sortOptions.find(({ field }) => field === sort.value);
  updateUrl({ compareField: sortOption, filter });

  isUpdating.value = false;
};

onMounted(() => {
  if (sort.value === closest.field) {
    // Use a timeout to ensure that the page:finish hook in the loading plugin is called before
    // showing a loading indicator when fetching the user's location.
    window.setTimeout(async () => {
      userLocation.value = await attemptToGetUserLocation();

      if (!userLocation.value) {
        await handleFilterChange({
          filter: filter.value,
          sort: props.sortOptions[0].field,
        });
      }
      else {
        await fetchLocations();
      }

      $loading.finish();
    }, 100);
  }
});

if (!isGuest.value) {
  watch(() => activeList.value?.id, () => {
    if (props.isFavorites) {
      handleListChange();
    }
  });
}

const { compareType, compareId } = params || {};

if (props.sortOptions?.length) {
  const canUseQueryCompareField = checkCompareField(
    queryCompareField,
    props.sortOptions,
    isAllAccess.value,
  );

  const canUseSortCookie = checkCompareField(
    sortCookie.value?.field,
    props.sortOptions,
    isAllAccess.value,
  );

  const canUseMapCookie = checkCompareField(
    mapSortCookie.value?.field,
    props.sortOptions,
    isAllAccess.value,
  );

  if (canUseQueryCompareField && queryCompareField) {
    sort.value = queryCompareField;
  }
  else if (canUseSortCookie && canUseMapCookie) {
    sort.value = mapSortCookie.value.time > sortCookie.value.time
      ? mapSortCookie.value.field
      : sortCookie.value.field;
  }
  else if (canUseSortCookie) {
    sort.value = sortCookie.value.field;
  }
  else if (canUseMapCookie) {
    sort.value = mapSortCookie.value.field;
  }
  else {
    sort.value = props.sortOptions[0].field;
  }
}

const parseComparePath = (path) => {
  const parts = path.split('/');
  const type = parts?.[2];
  const id = parts?.[3];
  const view = parts?.[4];
  return { type, id, view };
};

const isDifferentCompareView = (fromPath, toPath) => {
  const { type: fromCompareType, id: fromCompareId, view: fromCompareView } = parseComparePath(fromPath);
  const { type: toCompareType, id: toCompareId, view: toCompareView } = parseComparePath(toPath);
  return fromCompareType && toCompareType
    && fromCompareId && toCompareId
    && fromCompareView && toCompareView
    && fromCompareType === toCompareType
    && fromCompareId === toCompareId
    && fromCompareView !== toCompareView;
};

const isDifferentFavoritesView = (fromPath, toPath) => {
  if (String(fromPath).startsWith('/user/favorites/locations')
    && String(toPath).startsWith('/user/favorites/locations')) {
    const fromParts = fromPath.split('/');
    const toParts = toPath.split('/');
    const fromView = getLastItem(fromParts);
    const toView = getLastItem(toParts);

    return fromView && toView && fromView !== toView;
  }

  return false;
};

// Keep compare type and ID in the store when navigating to a new compare view for the same
// type and ID so that the <CompareHeader> doesn't briefly flash blank when the locations are
// reset. Note that this use case doesn't apply to User Favorite Locations pages because the
// compare type is not displayed in the <FavoritesHeader>, which is rendered on those pages.

const from = router.options.history.state.back;
const to = router.currentRoute.value.path;
let keepCompareOnReset = false;

if (from && to) {
  if (!props.isFavorites && isDifferentCompareView(from, to)) {
    keepCompareOnReset = true;
  }
}

// Refresh the page after 15 minutes of inactivity
useRefreshPage();

router.beforeEach((to, from) => {
  isNavigatingToDifferentView.value = props.isFavorites
    ? isDifferentFavoritesView(from.path, to.path)
    : isDifferentCompareView(from.path, to.path);

  isNavigatingToPowderFinder.value = to.path === '/explore/powder';
});

// If sorting by closest then show a loading indicator and fetch the locations on the
// client side in the mounted hook so access is available to the user's location.
if (sort.value !== closest.field) {
  // TODO: Consolidate code below into a getLocationsPayload() method and use here and in
  // fetchLocations() above?
  let location_type_ids;

  if (filter.value?.locationTypes?.length) {
    location_type_ids = filter.value.locationTypes.map(({ id }) => id).join(',');
  }

  const payload = {
    compareId,
    compareType,
    keepCompareOnReset,
    limit: limit.value,
    list_id: props.isFavorites ? activeList.value?.id : null,
    location_type_ids,
    refresh: true,
    resetLocations: true,
    skip: skip.value,
    sort: sort.value,
    units: units.value,
  };

  const { error } = await useAsyncData(() => locationsStore[props.handleFetchLocations](payload));

  if (error.value) {
    throw createError(parseOpenMountainApiError(error.value));
  }
}

// Reset dismissed skeleton count if user has removed all favorite locations
if (locationIdsForActiveList.value.length === 0) {
  dismissedSkeletonCount.value = null;
}

if (skeletonCount.value > 0) {
  skeletons.value = props.fetchLocationSkeletons(skeletonCount.value);
}
</script>

<template>
  <PageContent
    :can-show-select-favorite-list-dialog="canShowSelectFavoriteListDialog"
    class="tw-overflow-hidden"
    :can-get-user-banner="true"
    :page-params="pageParams"
    :style="pageContentStyle"
  >
    <FavoritesHeader
      v-if="isFavorites"
      :compare-field="compareField"
    />
    <CompareHeader
      v-else
      :compare-field="compareField"
    />
    <FavoritesNavigation
      v-if="isFavorites"
      :selected-tab="selectedTab"
    />
    <div
      v-if="showCompareMenu"
      class="tw-flex tw-items-center tw-mt-1.5"
    >
      <CompareOptions
        :key="compareFilterKey"
        v-model:filter="filter"
        v-model:sort="sort"
        container-class="tw-mr-1.5 tw-pr-1.5 tw-w-14 tw-border-r border-color"
        :is-favorites="isFavorites"
        :favorite-location-types="favoriteLocationTypes"
        :sort-options="sortOptions"
        @dropdown-close="handleDropdownClose"
        @dropdown-open="handleDropdownOpen"
        @update:model-value="handleFilterChange"
      />
      <CompareMenu
        :container-class="$style.compareMenuContainer"
        :nav-items="navItems"
        :selected-nav-item="selectedNavItem"
        @nav-item-click="handleNavItemClick"
      />
    </div>
    <div class="tw-mt-2.5 lg:tw-mt-7">
      <transition
        enter-active-class="fade-enter-transition"
        enter-from-class="fade-out"
        leave-active-class="fade-leave-transition"
        leave-to-class="fade-out"
        tag="div"
      >
        <div v-if="hasLocations || skeletons">
          <CompareLocationsList
            v-if="hasLocations"
            :card-component="cardComponent"
            :card-component-props="cardComponentProps"
            :fetch-locations="fetchLocations"
            :locations="locations"
            :range="range"
          />
          <ul v-if="skeletons && !isNavigatingToDifferentView">
            <li
              v-if="showNoFavoritesMessage"
              class="tw--mt-2 tw-mb-2.5"
            >
              <Card
                :has-body-padding="true"
              >
                <template #body>
                  <p class="tw-flex tw-items-center">
                    <font-awesome-icon
                      class="tw-h-8 tw-w-8 tw-mr-4 text-opensnow-blue-color"
                      :icon="['far', 'lightbulb-on']"
                    />
                    <span>
                      Add favorite locations, such as ski areas or towns, to view weather and snow conditions on one convenient screen.
                    </span>
                  </p>
                </template>
              </Card>
            </li>
            <li
              v-for="location in skeletons.locations"
              :key="location.id"
              class="tw-mb-2.5"
            >
              <Component
                :is="cardComponent"
                :location="location"
                :range="skeletons.range"
                v-bind="cardComponentProps"
                :is-skeleton="true"
                :show-close-button="canDismissSkeleton"
                @close-button-click="handleDismissSkeleton"
                @show-add-favorites-dialog="handleShowAddFavoritesDialog(true)"
              />
            </li>
          </ul>
        </div>
        <NoFavoritesMessage
          v-else-if="showNoFavoritesMessage"
          :card-component="cardComponent"
          class="tw-py-10 lg:tw-py-16"
          :compare-type="compareType"
          :is-favorites="isFavorites"
          :is-filter-set="isFilterSet"
          view="locations"
          @favorites-added="handleFavoritesAdded"
          @show-add-favorites-dialog="handleShowAddFavoritesDialog"
        />
      </transition>
    </div>
    <AddFavoritesDialog
      v-if="isFavorites && (skeletons || showAddFavoritesDialog)"
      :show-dialog="showAddFavoritesDialog"
      view="locations"
      @close="handleAddFavoritesDialogClose"
    />
  </PageContent>
</template>

<style module>
/*
 * The width of the compare menu container is 100% less the width + padding + border + margin of
 * the filter button. This allows the compare menu to be rendered in a fixed width parent element
 * so that it can be scrolled.
 */
.compareMenuContainer {
  width: calc(100% - 3.5rem - 0.375rem - 1px - 0.375rem);
}
</style>
